import { error, success } from 'react-toastify-redux';
import moment from 'moment';
import {
  ordersCollection,
  deliverySlotsCollection,
  archivesCollection,
  firebase,
  firestore,
} from '../../firebase';
import { startLoading, stopLoading, redirect } from './ui';
import { getAdmin } from '../../utils/auth';

export const DeliverySlotActionTypes = {
  GET_AVAILABLE_DELIVERY_SLOTS: 'GET_AVAILABLE_DELIVERY_SLOTS',
  GET_AVAILABLE_DELIVERY_SLOT_BY_DATE: 'GET_AVAILABLE_DELIVERY_SLOT_BY_DATE',
  GET_DELIVERY_SLOTS: 'GET_DELIVERY_SLOTS',
  GET_DELIVERY_SLOT: 'GET_DELIVERY_SLOT',
  CREATE_DELIVERY_SLOT: 'CREATE_DELIVERY_SLOT',
  UPDATE_DELIVERY_SLOT: 'UPDATE_DELIVERY_SLOT',
  DELETE_DELIVERY_SLOT: 'DELETE_DELIVERY_SLOT',
};

/**
 * Retrieve multiple delivery slots from firebase
 * @param {object} paging
 * @param {string} paging.orderBy order by field name
 * @param {string} paging.order get desc or asc
 * @param {number} paging.page current page
 * @param {number} paging.size per page size
 */
export const getAvailableDeliverySlots = (paging = {}) => {
  return async (dispatch) => {
    try {
      dispatch(startLoading());

      if (!paging.orderBy) paging.orderBy = 'date';
      if (!paging.order) paging.order = 'asc';
      if (!paging.page) paging.page = 1;
      if (!paging.size) paging.size = 1000;

      const response = await deliverySlotsCollection
        .where(
          'date',
          '>=',
          moment().startOf('year').subtract(2, 'months').toDate()
        )
        .limit(paging.size)
        .get();

      const items = [];

      response.forEach((doc) => {
        const data = doc.data();
        if (!data.slots) return;
        data.slots.map((s) => {
          s.count = s.orders.length;
          return s;
        });
        data.slots = data.slots.filter(
          (s) => s.count < (s.maxPerSlot || data.maxPerSlot)
        );
        if (data.slots.length > 0) items.push({ id: doc.id, ...data });
      });

      dispatch({
        type: DeliverySlotActionTypes.GET_AVAILABLE_DELIVERY_SLOTS,
        availableDeliverySlots: { items, paging },
      });
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};

/**
 * Retrieve single delivery slot from firebase
 * @param {string} id get by object id
 */
export const getAvailableDeliverySlotByDate = async (date, id) => {
  let deliverySlot = null;
  let doc = null;
  if (date) {
    date = moment(date).startOf('day').toDate();
    const documentSnapshots = await deliverySlotsCollection
      .where('date', '==', date)
      .get();

    if (documentSnapshots.docs.length) {
      [doc] = documentSnapshots.docs;
    }
  } else if (id) {
    doc = await deliverySlotsCollection.doc(id).get();
  }

  if (doc) {
    const data = doc.data();
    if (!data.slots) return null;
    data.slots.map((s) => {
      s.count = s.orders.length;
      return s;
    });
    data.slots = data.slots.filter(
      (s) => s.count < (s.maxPerSlot || data.maxPerSlot)
    );
    if (data.slots.length > 0) {
      deliverySlot = { id: doc.id, ...data };
    }
  }
  return deliverySlot;
};

/**
 * Re-read one delivery slot document straight from firebase, unfiltered.
 *
 * The slot lists in redux are a snapshot taken when the page loaded, so their
 * `count` goes stale while a modal sits open. Use this to re-check capacity at
 * the moment of saving rather than trusting that snapshot.
 *
 * Returns the same shape the modal already works with (`date`, `maxPerSlot`,
 * `slots[].count`), but keeps full slots in the list so the caller can tell
 * "this slot is full" apart from "this slot no longer exists".
 *
 * @param {string} id delivery slot document id
 */
export const getDeliverySlotCapacity = async (id) => {
  if (!id) return null;
  const doc = await deliverySlotsCollection.doc(id).get();
  if (!doc.exists) return null;
  const data = doc.data();
  return {
    id: doc.id,
    date: data.date,
    maxPerSlot: data.maxPerSlot,
    slots: (data.slots || []).map((s) => ({
      time: s.time,
      count: (s.orders || []).length,
      maxPerSlot: s.maxPerSlot,
      displayInStore: s.displayInStore,
      orders: (s.orders || []).map((o) => ({
        refNum: o.refNum,
        scheduleUuid: o.scheduleUuid,
      })),
    })),
  };
};

/**
 * The one place the effective cap for a slot is decided: a per-slot value
 * overrides the day default, a blank one falls back to it. Kept as a single
 * export so the slot picker and the order-save check cannot drift apart.
 *
 * Deliberately `||` and not `??`: a per-slot `"0"` is how a closed day is
 * currently expressed, and an empty string must keep falling back to the day
 * cap. Changing this needs the string -> number migration in the same release.
 */
export const effectiveSlotCapacity = (slot, snapshot) =>
  Number(slot.maxPerSlot || snapshot.maxPerSlot);

/**
 * Identity of a saved schedule. `uuid` is what the order trigger matches a
 * delivery slot entry on; `createdUnix` is the pre-uuid fallback it still
 * honours, so keep both paths here or legacy orders look "new" on every save.
 */
const scheduleKeyOf = (schedule) =>
  schedule.uuid ? `uuid:${schedule.uuid}` : `unix:${schedule.createdUnix}`;

/**
 * Which schedules on the form are asking for slot capacity they do not already
 * hold: ones added since the order was loaded, and ones moved to a different
 * date/time. An untouched schedule already occupies its slot — the order
 * trigger will match it by uuid and leave the entry where it is — so re-saving
 * the order takes up nothing new and must not be checked. Re-checking it is
 * what made an address edit fail against a now-full (often already delivered)
 * slot.
 *
 * @param {Array} schedules the schedules currently on the form
 * @param {Array} savedSchedules the schedules as the order was loaded
 * @returns {Array} the subset that needs a free slot
 */
export const getSchedulesNeedingCapacity = (schedules, savedSchedules) => {
  const saved = new Map();
  (savedSchedules || []).forEach((s) => {
    if (!s.id || !s.time) return;
    saved.set(scheduleKeyOf(s), `${s.id}|${s.time}`);
  });
  return (schedules || []).filter((s) => {
    if (!s.id || !s.time) return false;
    const before = saved.get(scheduleKeyOf(s));
    return !before || before !== `${s.id}|${s.time}`;
  });
};

/**
 * Re-check the order's schedules against live slot capacity.
 *
 * Called when the order is saved, because the modal's check happens when a
 * schedule is added to form state and the slot can fill in between. Only
 * schedules that newly want a place in a slot are charged against capacity
 * (see getSchedulesNeedingCapacity); untouched ones are counted as occupancy
 * they already hold, so moving a second schedule into a slot this order is
 * already in is still blocked when that slot is full. Groups by slot document
 * so each is read once, and ignores entries belonging to this order — the
 * order trigger reconciles those from the order's own schedules.
 *
 * @param {Array} schedules order.schedules as currently edited
 * @param {string} refNum this order's refNum (absent on a new order)
 * @param {Array} savedSchedules order.schedules as loaded (empty on a new order)
 * @returns {Promise<Array>} one entry per slot that would be exceeded
 */
export const findScheduleCapacityConflicts = async (
  schedules,
  refNum,
  savedSchedules
) => {
  const needCapacity = getSchedulesNeedingCapacity(schedules, savedSchedules);
  if (!needCapacity.length) return [];

  const wanted = new Map();
  needCapacity.forEach((s) => {
    const key = `${s.id}|${s.time}`;
    wanted.set(key, (wanted.get(key) || 0) + 1);
  });

  // schedules staying put still sit in their slot, so they count towards it
  const untouched = new Map();
  (schedules || []).forEach((s) => {
    if (!s.id || !s.time) return;
    if (needCapacity.includes(s)) return;
    const key = `${s.id}|${s.time}`;
    untouched.set(key, (untouched.get(key) || 0) + 1);
  });

  const snapshots = new Map();
  await Promise.all(
    [...new Set([...wanted.keys()].map((k) => k.split('|')[0]))].map(
      async (id) => snapshots.set(id, await getDeliverySlotCapacity(id))
    )
  );

  const conflicts = [];
  wanted.forEach((added, key) => {
    const [id, time] = key.split('|');
    const snapshot = snapshots.get(id);
    const slot = snapshot && snapshot.slots.find((x) => x.time === time);
    if (!snapshot || !slot) {
      conflicts.push({ id, time, missing: true });
      return;
    }
    const capacity = effectiveSlotCapacity(slot, snapshot);
    const others = slot.orders.filter(
      (o) => !refNum || o.refNum !== refNum
    ).length;
    const mine = added + (untouched.get(key) || 0);
    if (others + mine > capacity)
      conflicts.push({ id, time, date: snapshot.date, capacity, others, mine });
  });
  return conflicts;
};

/**
 * Retrieve multiple delivery slots from firebase
 * @param {string} paging.orderBy order by field name
 * @param {string} paging.order get desc or asc
 * @param {number} paging.page current page
 * @param {number} paging.size per page size
 */
export const getDeliverySlots = ({ orderBy, order, page, size, params }) => {
  return async (dispatch, getState) => {
    try {
      const {
        deliverySlots: { deliverySlots },
      } = getState();

      orderBy = orderBy || deliverySlots.paging.orderBy;
      order = order || deliverySlots.paging.order;
      page = page !== null ? page : deliverySlots.paging.page;
      size = size || deliverySlots.paging.size;
      params = params || deliverySlots.paging.params;

      // if nothing has changed, return
      if (
        deliverySlots.items.length - 1 >= page &&
        deliverySlots.paging.page === page &&
        deliverySlots.paging.order === order &&
        deliverySlots.paging.orderBy === orderBy &&
        deliverySlots.paging.size === size &&
        deliverySlots.paging.params === params
      )
        return;

      let items = [...deliverySlots.items];
      let { lastSnapshot } = deliverySlots;
      const paging = { page, order, orderBy, size, params };
      let { dateFrom, dateTo } = params;

      // if the page already has delivery slots data just update the paging
      if (
        deliverySlots.items.length - 1 >= page &&
        deliverySlots.paging.page !== page
      ) {
        dispatch({
          type: DeliverySlotActionTypes.GET_DELIVERY_SLOTS,
          deliverySlots: {
            items,
            paging,
            lastSnapshot,
          },
        });
        return;
      }

      dispatch(startLoading());

      let query = deliverySlotsCollection.orderBy(orderBy, order);
      if (dateFrom && dateTo) {
        dateFrom = moment(dateFrom).startOf('day').toDate();
        dateTo = moment(dateTo).endOf('day').toDate();
        query = query.where('date', '>=', dateFrom).where('date', '<=', dateTo);
      }

      if (
        deliverySlots.paging.page !== page &&
        deliverySlots.items.length - 1 < page
      ) {
        query = query.startAfter(lastSnapshot);
      } else {
        items = [];
        paging.page = 0;
      }

      const documentSnapshots = await query.limit(size).get();
      const results = [];

      documentSnapshots.docs.map(async (doc) => {
        const data = doc.data();
        results.push({ id: doc.id, ...data });
      });

      lastSnapshot = documentSnapshots.docs[documentSnapshots.docs.length - 1];

      if (results.length > 0) {
        items.push(results);

        dispatch({
          type: DeliverySlotActionTypes.GET_DELIVERY_SLOTS,
          deliverySlots: {
            items,
            paging,
            lastSnapshot,
          },
        });
      }
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};

/**
 * Retrieve single delivery slot from firebase
 * @param {string} id get by object id
 */
export const getDeliverySlot = (id) => {
  return async (dispatch) => {
    try {
      dispatch(startLoading());

      const doc = await deliverySlotsCollection.doc(id).get();
      const data = doc.data();

      if (data.slots) {
        await Promise.all(
          data.slots.map(async (s) => {
            if (s.refNum) {
              const orderRef = await ordersCollection
                .where('refNum', '==', s.refNum)
                .limit(1)
                .get();
              if (orderRef.docs.length) s.orderId = orderRef.docs[0].id;
            }
            return s;
          })
        );
      }

      const deliverySlot = {
        id: doc.id,
        ...data,
      };

      dispatch({
        type: DeliverySlotActionTypes.GET_DELIVERY_SLOT,
        deliverySlot,
      });
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};

/**
 * Create multiple slots
 * @param {Array} DeliverySlots
 */
export const createDeliverySlots = (items) => {
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      if (!items.length) return;

      const batch = firestore.batch();
      await Promise.all(
        items.map(async (item) => {
          // audit
          item.audit = {
            createdBy: getAdmin().name,
            createdOn: firebase.firestore.FieldValue.serverTimestamp(),
          };

          const existRef = await deliverySlotsCollection
            .where('date', '==', item.date)
            .get();
          if (existRef.docs.length) {
            const existingData = existRef.docs[0].data();

            const existingTimes = existingData.slots.map((s) => s.time);
            const newTimes = item.slots.map((s) => s.time);
            const allExistingTimesIncluded = existingTimes.every((time) =>
              newTimes.includes(time)
            );

            if (!allExistingTimesIncluded) {
              return;
            }

            item.slots = item.slots.map((s) => {
              const existingSlot = existingData.slots.find(
                (es) => es.time === s.time
              );
              if (existingSlot) {
                s.orders = existingSlot.orders;
              }
              return s;
            });
            batch.update(
              deliverySlotsCollection.doc(existRef.docs[0].id),
              item
            );
          } else {
            batch.set(deliverySlotsCollection.doc(), item);
          }
        })
      );
      await batch.commit();

      dispatch(redirect(`/delivery-slots`));
      setTimeout(() => dispatch(success('Successfully created!')), 200);
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};

export const updateDeliverySlot = (id, item) => {
  return async (dispatch) => {
    try {
      dispatch(startLoading());

      item.audit = {
        ...item.audit,
        modifiedBy: getAdmin().name,
        modifiedOn: firebase.firestore.FieldValue.serverTimestamp(),
      };

      item.slots.map((s) => {
        delete s.orderId;
        return s;
      });

      await deliverySlotsCollection.doc(id).set(item);
      setTimeout(() => dispatch(success('Successfully updated!')), 200);
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};

/**
 * Delete delivery slot
 * @param {string} id
 */
export const deleteDeliverySlot = (id) => {
  return async (dispatch) => {
    try {
      const deliverySlotRef = await deliverySlotsCollection.doc(id).get();
      const deliverySlot = deliverySlotRef.data();

      dispatch(startLoading());

      deliverySlot.audit = {
        ...deliverySlot.audit,
        deletedBy: getAdmin().name,
        deletedOn: firebase.firestore.FieldValue.serverTimestamp(),
      };

      const batch = firestore.batch();
      batch.delete(deliverySlotsCollection.doc(id));
      batch.update(archivesCollection.doc('deliverySlots'), {
        [id]: deliverySlot,
      });
      await batch.commit();

      dispatch({
        type: DeliverySlotActionTypes.DELETE_DELIVERY_SLOT,
      });
      dispatch(redirect('/delivery-slots'));

      setTimeout(() => dispatch(success('Successfully deleted!')), 200);
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};
